import { c as _c } from "react/compiler-runtime";
import { Drawer, Flex, FlexProps } from '@chakra-ui/react';
import { chakra } from '@chakra-ui/react';
import { css, Global } from '@emotion/react';
import { useIsMounted } from '@react-hookz/web';
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { AppLayoutPanelProvider, useParentAppLayoutPanel } from '@src/components/sco/AppLayout/Panel/AppLayoutPanelContext';
import { AppLayoutResizeHandle } from '@src/components/sco/AppLayout/ResizeHandle';
import { Dimensions, useResize } from '@src/components/sco/AppLayout/ResizeHandle/useResize';
import { BreakpointContextProvider, useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { motion, useReducedMotion, type Transition } from 'framer-motion';
import { usePathname } from 'next/navigation';
import React, { ReactNode, useEffect, useRef } from 'react';
export const MotionBox = chakra(motion.div) as React.ComponentType<any>;
export type AppLayoutPanelProps = {
  id?: string;
  children?: ReactNode;
  containerProps?: FlexProps;
  /** Merged onto the inner BreakpointContextProvider box (overflow, …). */
  innerBoxProps?: FlexProps;
  /** Optional coordinated motion for workspace mode changes. */
  panelTransition?: Transition;
  defaultWidth?: number | (() => number);
  drawerPlacement?: 'left' | 'right' | string;
  isDrawerOpen?: boolean;
  isPanelOpen?: boolean;
  isResizable?: boolean;
  isUsingDrawer?: boolean;
  maxWidth?: number;
  minWidth?: number;
  scrollable?: boolean;
  onDrawerClose?: () => void;
  /**
   * Asked before the drawer closes from its close button, Escape or a click outside. Return false to
   * keep it open, with its content still mounted (for example, to ask about unsaved changes first).
   */
  onDrawerCloseRequest?: () => boolean;
  onPanelClose?: () => void;
  onResize?: (size: Dimensions) => void;
  resizeHandlePosition?: 'left' | 'right' | string;
  resetScrollOnPathChange?: boolean;
  useDrawerBreakpoints?: any | {
    base?: boolean;
    sm?: boolean;
    md?: boolean;
    lg?: boolean;
    xl?: boolean;
  };
};
export const AppLayoutPanel: React.FC<AppLayoutPanelProps> = props => {
  const $ = _c(104);
  const layout = useParentAppLayout();
  const panel = useParentAppLayoutPanel();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isMounted = useIsMounted();
  const reducedMotion = useReducedMotion();
  const pathname = usePathname();
  const scrollRef = useRef(null);
  let t0;
  if ($[0] !== getBreakpointValue || $[1] !== props.useDrawerBreakpoints) {
    t0 = props.useDrawerBreakpoints ? getBreakpointValue(props.useDrawerBreakpoints) : false;
    $[0] = getBreakpointValue;
    $[1] = props.useDrawerBreakpoints;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const useDrawer = t0;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let namedContainerProps = t1;
  if (props.id?.endsWith("-main")) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        bgColor: "transparent",
        boxShadow: "none",
        border: "1px solid {colors.colorPalette.border}"
      };
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    namedContainerProps = t2;
  }
  if (props.id?.endsWith("-nav")) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        bgColor: "transparent",
        boxShadow: "none",
        border: "1px solid transparent",
        padding: [3, 3, 0]
      };
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    namedContainerProps = t2;
  }
  if (props.id?.endsWith("-aside")) {
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        bgColor: "transparent",
        boxShadow: "none",
        border: "1px solid {colors.colorPalette.border/50}"
      };
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    namedContainerProps = t2;
  }
  let t2;
  if ($[7] !== layout || $[8] !== panel || $[9] !== props.id || $[10] !== useDrawer) {
    t2 = () => {
      layout.updatePanel?.(props.id as string, {
        isUsingDrawer: useDrawer ? true : false,
        isDrawerOpen: false
      });
      if (layout && panel && useDrawer) {
        layout.closePanel?.(panel.id);
      }
    };
    $[7] = layout;
    $[8] = panel;
    $[9] = props.id;
    $[10] = useDrawer;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== useDrawer) {
    t3 = [useDrawer];
    $[12] = useDrawer;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  useEffect(t2, t3);
  let t4;
  if ($[14] !== props.resetScrollOnPathChange) {
    t4 = () => {
      if (props.resetScrollOnPathChange && scrollRef.current) {
        scrollRef.current.scrollTo(0, 0);
      }
    };
    $[14] = props.resetScrollOnPathChange;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== pathname) {
    t5 = [pathname];
    $[16] = pathname;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useEffect(t4, t5);
  let t6;
  if ($[18] !== props.defaultWidth) {
    t6 = typeof props.defaultWidth === "function" ? props.defaultWidth() : props.defaultWidth || 300;
    $[18] = props.defaultWidth;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const t7 = props.minWidth || 0;
  let t8;
  if ($[20] !== props.isResizable || $[21] !== props.maxWidth || $[22] !== props.onResize || $[23] !== props.resizeHandlePosition || $[24] !== t6 || $[25] !== t7) {
    t8 = {
      defaultWidth: t6,
      minWidth: t7,
      maxWidth: props.maxWidth,
      isResizable: props.isResizable,
      handlePosition: props.resizeHandlePosition,
      onResize: props.onResize
    };
    $[20] = props.isResizable;
    $[21] = props.maxWidth;
    $[22] = props.onResize;
    $[23] = props.resizeHandlePosition;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const resize = useResize(t8);
  let motionVariants;
  if ($[27] !== props.panelTransition || $[28] !== props.resizeHandlePosition || $[29] !== reducedMotion || $[30] !== resize) {
    motionVariants = {
      enter: {
        marginLeft: 0,
        marginRight: 0,
        transition: reducedMotion ? {
          duration: 0
        } : props.panelTransition || {
          type: "spring",
          duration: 0.5,
          bounce: 0.25
        }
      },
      exit: {
        marginLeft: 0,
        marginRight: 0,
        transition: reducedMotion ? {
          duration: 0
        } : props.panelTransition || {
          type: "ease-in",
          duration: 0.25,
          bounce: 0.25
        }
      }
    };
    if (props.resizeHandlePosition === "left") {
      motionVariants.enter.marginRight = 0;
      motionVariants.exit.marginRight = -resize.width;
    }
    if (props.resizeHandlePosition === "right") {
      motionVariants.enter.marginLeft = 0;
      motionVariants.exit.marginLeft = -resize.width;
    }
    $[27] = props.panelTransition;
    $[28] = props.resizeHandlePosition;
    $[29] = reducedMotion;
    $[30] = resize;
    $[31] = motionVariants;
  } else {
    motionVariants = $[31];
  }
  let t9;
  if ($[32] !== layout || $[33] !== props) {
    t9 = () => {
      layout.closeDrawer?.(props.id as string);
      if (props.onDrawerClose) {
        props.onDrawerClose();
      }
    };
    $[32] = layout;
    $[33] = props;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const onDrawerClose = t9;
  if (useDrawer) {
    const isLeft = props.drawerPlacement === "left" || props.resizeHandlePosition === "left";
    const t10 = props.id as string;
    const t11 = props.isDrawerOpen === true;
    const t12 = props.drawerPlacement as any || (props.resizeHandlePosition === "left" ? "end" : "start");
    let t13;
    if ($[35] !== onDrawerClose || $[36] !== props) {
      t13 = e => {
        if (!e.open) {
          if (props.onDrawerCloseRequest?.() === false) {
            return;
          }
          onDrawerClose();
        }
      };
      $[35] = onDrawerClose;
      $[36] = props;
      $[37] = t13;
    } else {
      t13 = $[37];
    }
    let t14;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Drawer.Backdrop backdropFilter="blur(10px)" />;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    const t15 = isLeft ? 0 : undefined;
    const t16 = !isLeft ? 0 : undefined;
    let t17;
    if ($[39] !== t15 || $[40] !== t16) {
      t17 = <Drawer.CloseTrigger left={t15} margin={4} position="absolute" right={t16} top={0} zIndex={9999} />;
      $[39] = t15;
      $[40] = t16;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    let t18;
    if ($[42] !== namedContainerProps || $[43] !== props.children || $[44] !== props.containerProps) {
      t18 = <Flex align="stretch" flexDirection="column" flexGrow={1} justify="stretch" overflowY="scroll" {...namedContainerProps} {...props.containerProps}>{props.children}</Flex>;
      $[42] = namedContainerProps;
      $[43] = props.children;
      $[44] = props.containerProps;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== t17 || $[47] !== t18) {
      t19 = <Drawer.Positioner><Drawer.Content maxWidth="85%" position="relative" width="85%">{t17}{t18}</Drawer.Content></Drawer.Positioner>;
      $[46] = t17;
      $[47] = t18;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    let t20;
    if ($[49] !== t11 || $[50] !== t12 || $[51] !== t13 || $[52] !== t19) {
      t20 = <Drawer.Root closeOnEscape={true} closeOnInteractOutside={true} open={t11} placement={t12} onOpenChange={t13}>{t14}{t19}</Drawer.Root>;
      $[49] = t11;
      $[50] = t12;
      $[51] = t13;
      $[52] = t19;
      $[53] = t20;
    } else {
      t20 = $[53];
    }
    let t21;
    if ($[54] !== t10 || $[55] !== t20) {
      t21 = <AppLayoutPanelProvider id={t10}>{t20}</AppLayoutPanelProvider>;
      $[54] = t10;
      $[55] = t20;
      $[56] = t21;
    } else {
      t21 = $[56];
    }
    return t21;
  }
  if (props.isResizable || !!props.resizeHandlePosition) {
    const t10 = props.id as string;
    let t11;
    if ($[57] !== props.isPanelOpen || $[58] !== props.resizeHandlePosition || $[59] !== resize) {
      t11 = props.isPanelOpen !== false && props.resizeHandlePosition === "left" && <AppLayoutResizeHandle {...resize.getHandleProps()} />;
      $[57] = props.isPanelOpen;
      $[58] = props.resizeHandlePosition;
      $[59] = resize;
      $[60] = t11;
    } else {
      t11 = $[60];
    }
    const t12 = !isMounted ? null : props.isPanelOpen !== false ? "enter" : "exit";
    const t13 = props.isPanelOpen === false ? "exit" : "enter";
    const t14 = motionVariants as any;
    const t15 = namedContainerProps;
    const t16 = props.containerProps;
    let t17;
    if ($[61] !== resize) {
      t17 = resize.getContainerProps();
      $[61] = resize;
      $[62] = t17;
    } else {
      t17 = $[62];
    }
    let t18;
    if ($[63] !== props.innerBoxProps) {
      t18 = {
        align: "stretch",
        alignSelf: "stretch",
        justifySelf: "stretch",
        display: "flex",
        justify: "stretch",
        height: "full",
        width: "full",
        ...props.innerBoxProps
      };
      $[63] = props.innerBoxProps;
      $[64] = t18;
    } else {
      t18 = $[64];
    }
    let t19;
    if ($[65] !== props.children || $[66] !== t18) {
      t19 = <BreakpointContextProvider boxProps={t18}>{props.children}</BreakpointContextProvider>;
      $[65] = props.children;
      $[66] = t18;
      $[67] = t19;
    } else {
      t19 = $[67];
    }
    let t20;
    if ($[68] !== namedContainerProps || $[69] !== props.containerProps || $[70] !== t12 || $[71] !== t13 || $[72] !== t14 || $[73] !== t17 || $[74] !== t19) {
      t20 = <MotionBox alignSelf="stretch" animate={t12} bgColor="colorPalette.panel" border="1px solid {colors.colorPalette.border/25}" borderRadius="lg" display="flex" flexShrink={1} initial={t13} justifySelf="stretch" overflowY="hidden" variants={t14} zIndex="1" {...t15} {...t16} {...t17}>{t19}</MotionBox>;
      $[68] = namedContainerProps;
      $[69] = props.containerProps;
      $[70] = t12;
      $[71] = t13;
      $[72] = t14;
      $[73] = t17;
      $[74] = t19;
      $[75] = t20;
    } else {
      t20 = $[75];
    }
    let t21;
    if ($[76] !== props.isPanelOpen || $[77] !== props.resizeHandlePosition || $[78] !== resize) {
      t21 = props.isPanelOpen !== false && props.resizeHandlePosition === "right" && <AppLayoutResizeHandle {...resize.getHandleProps()} />;
      $[76] = props.isPanelOpen;
      $[77] = props.resizeHandlePosition;
      $[78] = resize;
      $[79] = t21;
    } else {
      t21 = $[79];
    }
    let t22;
    if ($[80] !== resize.isResizing) {
      t22 = resize.isResizing && <Global styles={css`
              body * {
                -user-select: none;
                -webkit-user-select: none; /* Chrome/Safari */
                -moz-user-select: none; /* Firefox */
                -ms-user-select: none; /* IE10+ */
              }
            `} />;
      $[80] = resize.isResizing;
      $[81] = t22;
    } else {
      t22 = $[81];
    }
    let t23;
    if ($[82] !== t10 || $[83] !== t11 || $[84] !== t20 || $[85] !== t21 || $[86] !== t22) {
      t23 = <AppLayoutPanelProvider id={t10}>{t11}{t20}{t21}{t22}</AppLayoutPanelProvider>;
      $[82] = t10;
      $[83] = t11;
      $[84] = t20;
      $[85] = t21;
      $[86] = t22;
      $[87] = t23;
    } else {
      t23 = $[87];
    }
    return t23;
  } else {
    const t10 = props.id as string;
    const t11 = props.scrollable === false ? "full" : "auto";
    const t12 = `${props.id}-inner`;
    const t13 = props.minWidth ?? 0;
    const t14 = props.scrollable === false ? "hidden" : "auto";
    let t15;
    if ($[88] !== props.innerBoxProps) {
      t15 = {
        alignSelf: "stretch",
        display: "flex",
        height: "full",
        justifySelf: "stretch",
        minWidth: 0,
        overflowY: "auto",
        width: "full",
        ...props.innerBoxProps
      };
      $[88] = props.innerBoxProps;
      $[89] = t15;
    } else {
      t15 = $[89];
    }
    let t16;
    if ($[90] !== props.children || $[91] !== t15) {
      t16 = <BreakpointContextProvider boxProps={t15}>{props.children}</BreakpointContextProvider>;
      $[90] = props.children;
      $[91] = t15;
      $[92] = t16;
    } else {
      t16 = $[92];
    }
    let t17;
    if ($[93] !== props.containerProps || $[94] !== props.maxWidth || $[95] !== t11 || $[96] !== t12 || $[97] !== t13 || $[98] !== t14 || $[99] !== t16) {
      t17 = <Flex ref={scrollRef} align="stretch" alignSelf="stretch" bgColor="colorPalette.panel" border="1px solid {colors.colorPalette.border/25}" borderRadius="lg" flex={1} height={t11} id={t12} justify="stretch" justifySelf="stretch" maxWidth={props.maxWidth} minWidth={t13} overflowX="hidden" overflowY={t14} {...props.containerProps}>{t16}</Flex>;
      $[93] = props.containerProps;
      $[94] = props.maxWidth;
      $[95] = t11;
      $[96] = t12;
      $[97] = t13;
      $[98] = t14;
      $[99] = t16;
      $[100] = t17;
    } else {
      t17 = $[100];
    }
    let t18;
    if ($[101] !== t10 || $[102] !== t17) {
      t18 = <AppLayoutPanelProvider id={t10}>{t17}</AppLayoutPanelProvider>;
      $[101] = t10;
      $[102] = t17;
      $[103] = t18;
    } else {
      t18 = $[103];
    }
    return t18;
  }
};